import { neutralizeCsvFormula } from "@/lib/csv-safe";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
// pdf-lib is loaded lazily inside buildInvoicePdf — keeps ~700KB out of the route chunk.
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, FileText, Download, Send, CheckCircle2, XCircle, Users } from "lucide-react";
import { toast } from "sonner";
import {
  upsertContract, deleteContract, generateInvoice, updateInvoiceStatus, deleteInvoice,
  upsertClient, deleteClient, exportInvoicesCsv,
} from "@/lib/invoicing.functions";

export const Route = createFileRoute("/_authenticated/invoicing")({
  component: InvoicingPage,
  head: () => ({ meta: [{ title: "Invoicing — MissionReady" }] }),
});

type Site = { id: string; name: string };
type Rate = { site_id: string | null; service_type: string; hourly_rate_cents: number; gl_code: string | null };
type Client = {
  id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  billing_address: string | null;
  payment_terms_days: number;
  tax_id: string | null;
  default_tax_rate_bps: number;
  default_gl_code: string | null;
  notes: string | null;
  active: boolean;
};
type Contract = {
  id: string;
  client_id: string | null;
  name: string;
  client_name: string;
  client_email: string | null;
  client_address: string | null;
  currency: string;
  billing_cycle: string;
  site_ids: string[];
  monthly_retainer_cents: number;
  retainer_description: string | null;
  notes: string | null;
  active: boolean;
  ot_threshold_hours_per_week: number;
  ot_multiplier: number;
  auto_invoice: boolean;
  next_invoice_at: string | null;
  payment_terms_days: number;
  tax_rate_bps: number;
  gl_code: string | null;
};
type Invoice = {
  id: string;
  invoice_number: string;
  client_name: string;
  client_email: string | null;
  client_address: string | null;
  status: string;
  currency: string;
  period_start: string;
  period_end: string;
  issued_at: string | null;
  due_at: string | null;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  notes: string | null;
  contract_id: string | null;
  created_at: string;
};
type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_rate_cents: number;
  amount_cents: number;
  site_id: string | null;
};

const fmtMoney = (cents: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);

function InvoicingPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState("invoices");
  const [sites, setSites] = useState<Site[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [editingRates, setEditingRates] = useState<Rate[]>([]);
  const [contractOpen, setContractOpen] = useState(false);
  const [genFor, setGenFor] = useState<Contract | null>(null);
  const [viewing, setViewing] = useState<Invoice | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const exportInvoicesCsvFn = useServerFn(exportInvoicesCsv);

  const canManage =
    isPlatformAdmin || currentRole === "org_owner" || currentRole === "admin";

  const refresh = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const [s, c, i, cl] = await Promise.all([
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).eq("active", true).order("name"),
      supabase.from("billing_contracts").select("*").eq("organization_id", currentOrg.id).order("created_at", { ascending: false }),
      supabase.from("client_invoices").select("*").eq("organization_id", currentOrg.id).order("created_at", { ascending: false }).limit(200),
      supabase.from("billing_clients").select("*").eq("organization_id", currentOrg.id).order("name"),
    ]);
    setSites((s.data as Site[]) ?? []);
    setContracts((c.data as Contract[]) ?? []);
    setInvoices((i.data as Invoice[]) ?? []);
    setClients((cl.data as Client[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [currentOrg?.id]);

  if (!currentOrg) return null;

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Invoicing</h1>
        <p className="text-muted-foreground text-sm">Bill your clients for guard hours and retainers.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="clients">Clients</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div>
                <CardTitle>Invoices</CardTitle>
                <CardDescription>Generated from contracts and time entries.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {canManage && selectedInvoiceIds.size > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">{selectedInvoiceIds.size} selected</span>
                    <Button variant="outline" size="sm" onClick={() => exportGl("quickbooks")}>
                      <Download className="h-4 w-4 mr-1" /> QuickBooks CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportGl("xero")}>
                      <Download className="h-4 w-4 mr-1" /> Xero CSV
                    </Button>
                  </>
                )}
                {canManage && (
                  <Button
                    disabled={contracts.length === 0}
                    onClick={() => setGenFor(contracts[0])}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Generate invoice
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : invoices.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No invoices yet. {contracts.length === 0 ? "Add a contract first." : "Click Generate invoice to create one."}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <Checkbox
                          checked={selectedInvoiceIds.size === invoices.length && invoices.length > 0}
                          onCheckedChange={(ck) =>
                            setSelectedInvoiceIds(ck ? new Set(invoices.map((i) => i.id)) : new Set())
                          }
                        />
                      </TableHead>
                      <TableHead>Number</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedInvoiceIds.has(inv.id)}
                            onCheckedChange={(ck) => {
                              const next = new Set(selectedInvoiceIds);
                              if (ck) next.add(inv.id); else next.delete(inv.id);
                              setSelectedInvoiceIds(next);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.client_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {inv.period_start} → {inv.period_end}
                        </TableCell>
                        <TableCell>{fmtMoney(inv.total_cents, inv.currency)}</TableCell>
                        <TableCell><StatusBadge status={inv.status} /></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setViewing(inv)}>
                            <FileText className="h-4 w-4 mr-1" /> Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="contracts" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Contracts</CardTitle>
                <CardDescription>Per-client rate cards and retainers.</CardDescription>
              </div>
              {canManage && (
                <Button onClick={() => { setEditing(blankContract()); setEditingRates([]); setContractOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> New contract
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : contracts.length === 0 ? (
                <p className="text-muted-foreground text-sm">No contracts yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Sites</TableHead>
                      <TableHead>Retainer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>{c.client_name}</TableCell>
                        <TableCell className="text-xs">{c.site_ids.length}</TableCell>
                        <TableCell>{c.monthly_retainer_cents > 0 ? fmtMoney(c.monthly_retainer_cents, c.currency) : "—"}</TableCell>
                        <TableCell>{c.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                        <TableCell className="text-right space-x-1">
                          {canManage && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setGenFor(c)}>
                                <FileText className="h-4 w-4 mr-1" /> Invoice
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>Edit</Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Clients</CardTitle>
                <CardDescription>Bill-to entities with payment terms and tax details.</CardDescription>
              </div>
              {canManage && (
                <Button onClick={() => setEditingClient(blankClient())}>
                  <Users className="h-4 w-4 mr-2" /> New client
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : clients.length === 0 ? (
                <p className="text-muted-foreground text-sm">No clients yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Terms</TableHead>
                      <TableHead>Tax</TableHead>
                      <TableHead>GL</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((cl) => (
                      <TableRow key={cl.id}>
                        <TableCell className="font-medium">{cl.name}</TableCell>
                        <TableCell className="text-xs">{cl.contact_name ?? "—"}<br/><span className="text-muted-foreground">{cl.email ?? ""}</span></TableCell>
                        <TableCell>Net {cl.payment_terms_days}</TableCell>
                        <TableCell>{(cl.default_tax_rate_bps / 100).toFixed(2)}%</TableCell>
                        <TableCell className="font-mono text-xs">{cl.default_gl_code ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {canManage && <Button variant="ghost" size="sm" onClick={() => setEditingClient(cl)}>Edit</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {editing && (
        <ContractDialog
          open={contractOpen}
          onOpenChange={(o) => { setContractOpen(o); if (!o) setEditing(null); }}
          orgId={currentOrg.id}
          sites={sites}
          clients={clients}
          contract={editing}
          rates={editingRates}
          onSaved={() => { setContractOpen(false); setEditing(null); refresh(); }}
        />
      )}

      {genFor && (
        <GenerateDialog
          contract={genFor}
          contracts={contracts}
          onClose={() => setGenFor(null)}
          onGenerated={() => { setGenFor(null); refresh(); setTab("invoices"); }}
        />
      )}

      {viewing && (
        <InvoiceDialog
          invoice={viewing}
          onClose={() => setViewing(null)}
          onChanged={() => { setViewing(null); refresh(); }}
          canManage={canManage}
        />
      )}

      {editingClient && (
        <ClientDialog
          client={editingClient}
          orgId={currentOrg.id}
          onClose={() => setEditingClient(null)}
          onSaved={() => { setEditingClient(null); refresh(); }}
        />
      )}
    </div>
  );

  async function exportGl(format: "quickbooks" | "xero") {
    if (selectedInvoiceIds.size === 0) return;
    try {
      const r = await exportInvoicesCsvFn({
        data: { invoice_ids: Array.from(selectedInvoiceIds), format, mark_exported: true },
      });
      const blob = new Blob([r.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gl-export-${format}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.invoice_count} invoice(s) to ${format}`);
      setSelectedInvoiceIds(new Set());
      refresh();
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    }
  }

  async function openEdit(c: Contract) {
    setEditing(c);
    const { data } = await supabase
      .from("billing_contract_rates")
      .select("site_id, service_type, hourly_rate_cents, gl_code")
      .eq("contract_id", c.id);
    setEditingRates((data as Rate[]) ?? []);
    setContractOpen(true);
  }
}

function blankContract(): Contract {
  return {
    id: "",
    client_id: null,
    name: "",
    client_name: "",
    client_email: null,
    client_address: null,
    currency: "USD",
    billing_cycle: "monthly",
    site_ids: [],
    monthly_retainer_cents: 0,
    retainer_description: null,
    notes: null,
    active: true,
    ot_threshold_hours_per_week: 40,
    ot_multiplier: 1,
    auto_invoice: false,
    next_invoice_at: null,
    payment_terms_days: 30,
    tax_rate_bps: 0,
    gl_code: null,
  };
}

function StatusBadge({ status }: { status: string }) {
  const v: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    draft: "secondary", sent: "outline", paid: "default", void: "destructive",
  };
  return <Badge variant={v[status] ?? "secondary"}>{status}</Badge>;
}

function ContractDialog({
  open, onOpenChange, orgId, sites, clients, contract, rates, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  orgId: string;
  sites: Site[];
  clients: Client[];
  contract: Contract;
  rates: Rate[];
  onSaved: () => void;
}) {
  const save = useServerFn(upsertContract);
  const del = useServerFn(deleteContract);
  const [c, setC] = useState<Contract>(contract);
  const [rs, setRs] = useState<Rate[]>(rates.length > 0 ? rates : [{ site_id: null, service_type: "standard", hourly_rate_cents: 3500, gl_code: null }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setC(contract); setRs(rates.length > 0 ? rates : [{ site_id: null, service_type: "standard", hourly_rate_cents: 3500, gl_code: null }]); }, [contract, rates]);

  const applyClient = (id: string) => {
    if (id === "__none") { setC({ ...c, client_id: null }); return; }
    const cl = clients.find((x) => x.id === id);
    if (!cl) return;
    setC({
      ...c,
      client_id: cl.id,
      client_name: cl.name,
      client_email: cl.email,
      client_address: cl.billing_address,
      payment_terms_days: cl.payment_terms_days,
      tax_rate_bps: cl.default_tax_rate_bps,
      gl_code: cl.default_gl_code,
    });
  };

  const submit = async () => {
    if (!c.name || !c.client_name) { toast.error("Name and client name required"); return; }
    setSaving(true);
    try {
      await save({
        data: {
          id: c.id || undefined,
          organization_id: orgId,
          client_id: c.client_id,
          name: c.name,
          client_name: c.client_name,
          client_email: c.client_email || null,
          client_address: c.client_address || null,
          currency: c.currency,
          billing_cycle: c.billing_cycle as any,
          site_ids: c.site_ids,
          monthly_retainer_cents: c.monthly_retainer_cents,
          retainer_description: c.retainer_description || null,
          notes: c.notes || null,
          active: c.active,
          ot_threshold_hours_per_week: c.ot_threshold_hours_per_week,
          ot_multiplier: c.ot_multiplier,
          auto_invoice: c.auto_invoice,
          next_invoice_at: c.next_invoice_at,
          payment_terms_days: c.payment_terms_days,
          tax_rate_bps: c.tax_rate_bps,
          gl_code: c.gl_code || null,
          rates: rs.filter((r) => r.hourly_rate_cents > 0),
        },
      });
      toast.success("Contract saved");
      onSaved();
    } catch (e: any) { toast.error(e?.message ?? "Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!c.id || !confirm("Delete this contract?")) return;
    try { await del({ data: { id: c.id } }); toast.success("Deleted"); onSaved(); }
    catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{c.id ? "Edit contract" : "New contract"}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          {clients.length > 0 && (
            <div>
              <Label>Bill-to client</Label>
              <Select value={c.client_id ?? "__none"} onValueChange={applyClient}>
                <SelectTrigger aria-label="Bill-to client"><SelectValue placeholder="Free-text client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Free-text (no client record) —</SelectItem>
                  {clients.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Picking a client autofills name, address, terms, tax and GL.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Contract name</Label>
              <Input aria-label="Contract name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} placeholder="Acme Tower – 2026" />
            </div>
            <div>
              <Label>Client name</Label>
              <Input aria-label="Client name" value={c.client_name} onChange={(e) => setC({ ...c, client_name: e.target.value })} />
            </div>
            <div>
              <Label>Client email</Label>
              <Input aria-label="Client email" type="email" value={c.client_email ?? ""} onChange={(e) => setC({ ...c, client_email: e.target.value || null })} />
            </div>
            <div>
              <Label>Currency</Label>
              <Input aria-label="Currency" value={c.currency} maxLength={3} onChange={(e) => setC({ ...c, currency: e.target.value.toUpperCase() })} />
            </div>
            <div className="col-span-2">
              <Label>Client address</Label>
              <Textarea aria-label="Client address" rows={2} value={c.client_address ?? ""} onChange={(e) => setC({ ...c, client_address: e.target.value || null })} />
            </div>
            <div>
              <Label>Billing cycle</Label>
              <Select value={c.billing_cycle} onValueChange={(v) => setC({ ...c, billing_cycle: v })}>
                <SelectTrigger aria-label="Billing cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monthly retainer</Label>
              <Input aria-label="Monthly retainer"
                type="number" min={0} step="0.01"
                value={(c.monthly_retainer_cents / 100).toFixed(2)}
                onChange={(e) => setC({ ...c, monthly_retainer_cents: Math.round(parseFloat(e.target.value || "0") * 100) })}
              />
            </div>
            <div className="col-span-2">
              <Label>Retainer description</Label>
              <Input aria-label="Retainer description" value={c.retainer_description ?? ""} onChange={(e) => setC({ ...c, retainer_description: e.target.value || null })} placeholder="Monthly base coverage" />
            </div>
          </div>

          <div>
            <Label>Covered sites</Label>
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
              {sites.length === 0 ? <p className="text-xs text-muted-foreground p-2">No sites yet</p> : sites.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={c.site_ids.includes(s.id)}
                    onCheckedChange={(ck) => setC({
                      ...c,
                      site_ids: ck ? [...c.site_ids, s.id] : c.site_ids.filter((x) => x !== s.id),
                    })}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Rate card</Label>
              <Button type="button" size="sm" variant="outline"
                onClick={() => setRs([...rs, { site_id: null, service_type: "standard", hourly_rate_cents: 0, gl_code: null }])}>
                <Plus className="h-3 w-3 mr-1" /> Add rate
              </Button>
            </div>
            <div className="space-y-2">
              {rs.map((r, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_120px_40px] gap-2 items-center">
                  <Select
                    value={r.site_id ?? "__default"}
                    onValueChange={(v) => setRs(rs.map((x, i) => i === idx ? { ...x, site_id: v === "__default" ? null : v } : x))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default">Default (any site)</SelectItem>
                      {sites.filter((s) => c.site_ids.includes(s.id)).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={r.service_type}
                    onChange={(e) => setRs(rs.map((x, i) => i === idx ? { ...x, service_type: e.target.value } : x))}
                    placeholder="standard / armed / patrol"
                  />
                  <Input
                    type="number" min={0} step="0.01"
                    value={(r.hourly_rate_cents / 100).toFixed(2)}
                    onChange={(e) => setRs(rs.map((x, i) => i === idx ? { ...x, hourly_rate_cents: Math.round(parseFloat(e.target.value || "0") * 100) } : x))}
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setRs(rs.filter((_, i) => i !== idx))} aria-label="Remove rate row">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Per hour, in {c.currency}. "Default" applies to any covered site without a more specific rate.</p>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-4">
            <div>
              <Label>Payment terms (days)</Label>
              <Input aria-label="Payment terms (days)" type="number" min={0} value={c.payment_terms_days}
                onChange={(e) => setC({ ...c, payment_terms_days: parseInt(e.target.value || "0", 10) })} />
            </div>
            <div>
              <Label>Tax rate (%)</Label>
              <Input aria-label="Tax rate (%)" type="number" min={0} step="0.01" value={(c.tax_rate_bps / 100).toFixed(2)}
                onChange={(e) => setC({ ...c, tax_rate_bps: Math.round(parseFloat(e.target.value || "0") * 100) })} />
            </div>
            <div>
              <Label>OT threshold (hours/week)</Label>
              <Input aria-label="OT threshold (hours/week)" type="number" min={0} step="0.5" value={c.ot_threshold_hours_per_week}
                onChange={(e) => setC({ ...c, ot_threshold_hours_per_week: parseFloat(e.target.value || "0") })} />
            </div>
            <div>
              <Label>OT multiplier</Label>
              <Input aria-label="OT multiplier" type="number" min={1} step="0.05" value={c.ot_multiplier}
                onChange={(e) => setC({ ...c, ot_multiplier: parseFloat(e.target.value || "1") })} />
            </div>
            <div>
              <Label>Default GL code</Label>
              <Input aria-label="Default GL code" value={c.gl_code ?? ""} onChange={(e) => setC({ ...c, gl_code: e.target.value || null })} placeholder="4000-SECURITY" />
            </div>
            <div>
              <Label>Next auto-invoice run</Label>
              <Input aria-label="Next auto-invoice run" type="datetime-local"
                value={c.next_invoice_at ? c.next_invoice_at.slice(0, 16) : ""}
                onChange={(e) => setC({ ...c, next_invoice_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea aria-label="Notes" rows={2} value={c.notes ?? ""} onChange={(e) => setC({ ...c, notes: e.target.value || null })} />
          </div>

          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox checked={c.active} onCheckedChange={(ck) => setC({ ...c, active: !!ck })} /> Active
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={c.auto_invoice} onCheckedChange={(ck) => setC({ ...c, auto_invoice: !!ck })} /> Auto-generate per cycle
            </label>
          </div>
        </div>


        <DialogFooter className="gap-2">
          {c.id && <Button variant="destructive" onClick={remove}>Delete</Button>}
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateDialog({
  contract, contracts, onClose, onGenerated,
}: {
  contract: Contract;
  contracts: Contract[];
  onClose: () => void;
  onGenerated: () => void;
}) {
  const gen = useServerFn(generateInvoice);
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [contractId, setContractId] = useState(contract.id);
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(lastOfMonth);
  const [retainer, setRetainer] = useState(true);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      const r = await gen({ data: { contract_id: contractId, period_start: start, period_end: end, include_retainer: retainer } });
      toast.success(`Invoice ${r.invoice_number} created with ${r.line_count} line(s)`);
      onGenerated();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Generate invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Contract</Label>
            <Select value={contractId} onValueChange={setContractId}>
              <SelectTrigger aria-label="Contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Period start</Label><Input aria-label="Period start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label>Period end</Label><Input aria-label="Period end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={retainer} onCheckedChange={(c) => setRetainer(!!c)} /> Include monthly retainer
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={go} disabled={busy || !contractId}>{busy ? "Generating…" : "Generate"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({
  invoice, onClose, onChanged, canManage,
}: {
  invoice: Invoice;
  onClose: () => void;
  onChanged: () => void;
  canManage: boolean;
}) {
  const updateStatus = useServerFn(updateInvoiceStatus);
  const del = useServerFn(deleteInvoice);
  const [items, setItems] = useState<LineItem[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase
      .from("client_invoice_line_items")
      .select("id, description, quantity, unit, unit_rate_cents, amount_cents, site_id")
      .eq("invoice_id", invoice.id)
      .order("sort_order")
      .then(({ data }) => setItems((data as LineItem[]) ?? []));
  }, [invoice.id]);

  const setStatus = async (status: string) => {
    setBusy(true);
    try { await updateStatus({ data: { id: invoice.id, status: status as any } }); toast.success("Updated"); onChanged(); }
    catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this invoice?")) return;
    try { await del({ data: { id: invoice.id } }); toast.success("Deleted"); onChanged(); }
    catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const downloadPdf = async () => {
    const bytes = await buildInvoicePdf(invoice, items);
    const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${invoice.invoice_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = () => {
    const rows = [
      ["Description", "Quantity", "Unit", "Rate", "Amount"],
      ...items.map((i) => [i.description, i.quantity, i.unit, (i.unit_rate_cents / 100).toFixed(2), (i.amount_cents / 100).toFixed(2)]),
      [],
      ["Subtotal", "", "", "", (invoice.subtotal_cents / 100).toFixed(2)],
      ["Total", "", "", "", (invoice.total_cents / 100).toFixed(2)],
    ];
    const csv = rows.map((r) => r.map((c) => `"${neutralizeCsvFormula(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${invoice.invoice_number}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{invoice.invoice_number}</span>
            <StatusBadge status={invoice.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Bill to</p>
              <p className="font-medium">{invoice.client_name}</p>
              {invoice.client_email && <p className="text-xs">{invoice.client_email}</p>}
              {invoice.client_address && <p className="text-xs whitespace-pre-line">{invoice.client_address}</p>}
            </div>
            <div className="text-right">
              <p className="text-muted-foreground">Period</p>
              <p>{invoice.period_start} → {invoice.period_end}</p>
              {invoice.issued_at && <p className="text-xs text-muted-foreground mt-1">Issued {invoice.issued_at}</p>}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.description}</TableCell>
                  <TableCell className="text-right">{i.quantity} {i.unit}</TableCell>
                  <TableCell className="text-right">{fmtMoney(i.unit_rate_cents, invoice.currency)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(i.amount_cents, invoice.currency)}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-sm">No line items</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(invoice.subtotal_cents, invoice.currency)}</span></div>
              {invoice.tax_cents > 0 && (
                <div className="flex justify-between"><span>Tax</span><span>{fmtMoney(invoice.tax_cents, invoice.currency)}</span></div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span>{fmtMoney(invoice.total_cents, invoice.currency)}</span></div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={downloadPdf}><Download className="h-4 w-4 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={downloadCsv}><Download className="h-4 w-4 mr-1" /> CSV</Button>
          {canManage && invoice.status === "draft" && (
            <Button variant="outline" onClick={() => setStatus("sent")} disabled={busy}><Send className="h-4 w-4 mr-1" /> Mark sent</Button>
          )}
          {canManage && invoice.status !== "paid" && invoice.status !== "void" && (
            <Button onClick={() => setStatus("paid")} disabled={busy}><CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid</Button>
          )}
          {canManage && invoice.status !== "void" && (
            <Button variant="ghost" onClick={() => setStatus("void")} disabled={busy}><XCircle className="h-4 w-4 mr-1" /> Void</Button>
          )}
          {canManage && <Button variant="destructive" onClick={remove}>Delete</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function buildInvoicePdf(invoice: Invoice, items: LineItem[]): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const pdf = await PDFDocument.create();
  const page = pdf.addPage([612, 792]); // Letter
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const black = rgb(0, 0, 0);
  const grey = rgb(0.45, 0.45, 0.5);
  let y = 750;
  page.drawText("INVOICE", { x: 50, y, size: 22, font: bold, color: black });
  page.drawText(invoice.invoice_number, { x: 450, y, size: 12, font: bold, color: black });
  y -= 30;

  page.drawText("Bill to:", { x: 50, y, size: 10, font: bold, color: grey });
  page.drawText(`Period: ${invoice.period_start} → ${invoice.period_end}`, { x: 380, y, size: 10, font, color: grey });
  y -= 14;
  page.drawText(invoice.client_name, { x: 50, y, size: 11, font, color: black });
  if (invoice.issued_at) page.drawText(`Issued: ${invoice.issued_at}`, { x: 380, y, size: 10, font, color: grey });
  y -= 12;
  if (invoice.client_email) { page.drawText(invoice.client_email, { x: 50, y, size: 10, font, color: grey }); y -= 12; }
  if (invoice.client_address) {
    for (const line of invoice.client_address.split("\n").slice(0, 4)) {
      page.drawText(line, { x: 50, y, size: 10, font, color: grey });
      y -= 12;
    }
  }
  y -= 20;

  // Table header
  page.drawText("Description", { x: 50, y, size: 10, font: bold, color: black });
  page.drawText("Qty", { x: 360, y, size: 10, font: bold, color: black });
  page.drawText("Rate", { x: 420, y, size: 10, font: bold, color: black });
  page.drawText("Amount", { x: 510, y, size: 10, font: bold, color: black });
  y -= 6;
  page.drawLine({ start: { x: 50, y }, end: { x: 562, y }, thickness: 0.5, color: grey });
  y -= 14;

  const cur = invoice.currency;
  for (const it of items) {
    if (y < 100) { y = 750; pdf.addPage([612, 792]); }
    const desc = it.description.length > 60 ? it.description.slice(0, 57) + "…" : it.description;
    page.drawText(desc, { x: 50, y, size: 10, font, color: black });
    page.drawText(`${it.quantity} ${it.unit}`, { x: 360, y, size: 10, font, color: black });
    page.drawText(fmtMoney(it.unit_rate_cents, cur), { x: 420, y, size: 10, font, color: black });
    page.drawText(fmtMoney(it.amount_cents, cur), { x: 510, y, size: 10, font, color: black });
    y -= 16;
  }

  y -= 10;
  page.drawLine({ start: { x: 360, y }, end: { x: 562, y }, thickness: 0.5, color: grey });
  y -= 16;
  page.drawText("Subtotal", { x: 420, y, size: 10, font, color: black });
  page.drawText(fmtMoney(invoice.subtotal_cents, cur), { x: 510, y, size: 10, font, color: black });
  y -= 16;
  page.drawText("Total", { x: 420, y, size: 12, font: bold, color: black });
  page.drawText(fmtMoney(invoice.total_cents, cur), { x: 510, y, size: 12, font: bold, color: black });

  return pdf.save();
}

function blankClient(): Client {
  return {
    id: "",
    name: "",
    contact_name: null,
    email: null,
    phone: null,
    billing_address: null,
    payment_terms_days: 30,
    tax_id: null,
    default_tax_rate_bps: 0,
    default_gl_code: null,
    notes: null,
    active: true,
  };
}

function ClientDialog({
  client, orgId, onClose, onSaved,
}: {
  client: Client; orgId: string; onClose: () => void; onSaved: () => void;
}) {
  const save = useServerFn(upsertClient);
  const del = useServerFn(deleteClient);
  const [cl, setCl] = useState<Client>(client);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCl(client); }, [client]);

  const submit = async () => {
    if (!cl.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await save({ data: {
        id: cl.id || undefined,
        organization_id: orgId,
        name: cl.name,
        contact_name: cl.contact_name || null,
        email: cl.email || null,
        phone: cl.phone || null,
        billing_address: cl.billing_address || null,
        payment_terms_days: cl.payment_terms_days,
        tax_id: cl.tax_id || null,
        default_tax_rate_bps: cl.default_tax_rate_bps,
        default_gl_code: cl.default_gl_code || null,
        notes: cl.notes || null,
        active: cl.active,
      }});
      toast.success("Client saved"); onSaved();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!cl.id || !confirm("Delete this client?")) return;
    try { await del({ data: { id: cl.id } }); toast.success("Deleted"); onSaved(); }
    catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{cl.id ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input aria-label="Name" value={cl.name} onChange={(e) => setCl({ ...cl, name: e.target.value })} /></div>
            <div><Label>Contact</Label><Input aria-label="Contact" value={cl.contact_name ?? ""} onChange={(e) => setCl({ ...cl, contact_name: e.target.value || null })} /></div>
            <div><Label>Email</Label><Input aria-label="Email" type="email" value={cl.email ?? ""} onChange={(e) => setCl({ ...cl, email: e.target.value || null })} /></div>
            <div><Label>Phone</Label><Input aria-label="Phone" value={cl.phone ?? ""} onChange={(e) => setCl({ ...cl, phone: e.target.value || null })} /></div>
            <div className="col-span-2"><Label>Billing address</Label>
              <Textarea aria-label="Billing address" rows={2} value={cl.billing_address ?? ""} onChange={(e) => setCl({ ...cl, billing_address: e.target.value || null })} />
            </div>
            <div><Label>Payment terms (days)</Label>
              <Input aria-label="Payment terms (days)" type="number" min={0} value={cl.payment_terms_days}
                onChange={(e) => setCl({ ...cl, payment_terms_days: parseInt(e.target.value || "0", 10) })} />
            </div>
            <div><Label>Default tax (%)</Label>
              <Input aria-label="Default tax (%)" type="number" min={0} step="0.01" value={(cl.default_tax_rate_bps / 100).toFixed(2)}
                onChange={(e) => setCl({ ...cl, default_tax_rate_bps: Math.round(parseFloat(e.target.value || "0") * 100) })} />
            </div>
            <div><Label>Tax ID</Label><Input aria-label="Tax ID" value={cl.tax_id ?? ""} onChange={(e) => setCl({ ...cl, tax_id: e.target.value || null })} /></div>
            <div><Label>Default GL code</Label>
              <Input aria-label="Default GL code" value={cl.default_gl_code ?? ""} onChange={(e) => setCl({ ...cl, default_gl_code: e.target.value || null })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={cl.active} onCheckedChange={(ck) => setCl({ ...cl, active: !!ck })} /> Active
          </label>
        </div>
        <DialogFooter className="gap-2">
          {cl.id && <Button variant="destructive" onClick={remove}>Delete</Button>}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
